import { Button, IconButton, Menu, MenuItem } from "@material-ui/core";
import moment from "moment";
import React, { Component, Fragment } from "react";

import { ActionPermissionsObject } from "common/interfaces";
import WorkspaceController from "js/controllers/WorkspaceController";
import getLogger, { LogGroup } from "js/core/logger";
import { PresentationPrivacyType } from "js/core/models/presentation";
import NotificationsService from "js/core/services/notifications";
import { ClipboardType, clipboardWrite } from "js/core/utilities/clipboard";
import { trackActivity } from "js/core/utilities/utilities";
import { app } from "js/namespaces";
import Avatar from "js/react/components/Avatar";
import { ShowConfirmationDialog, ShowDialog, ShowUpgradeDialog, ShowWarningDialog } from "js/react/components/Dialogs/BaseDialog";
import ConfirmationDialog from "js/react/components/Dialogs/ConfirmationDialog";
import { ShowSnackBar } from "js/react/components/Dialogs/SnackBar";
import { FlexSpacer, Gap10 } from "js/react/components/Gap";
import Icon from "js/react/components/Icon";
import ProBadge from "js/react/components/ProBadge";
import { UpgradePlanDialogType } from "js/react/views/MarketingDialogs/UpgradePlanDialog";
import linkDS, { LinkType } from "js/react/views/PresentationSettings/dataservices/LinkDataService";
import EditEmbedCodeDialog from "js/react/views/PresentationSettings/dialogs/EditEmbedCodeDialog";
import EditInviteDialog from "js/react/views/PresentationSettings/dialogs/EditInviteDialog";
import EditLinkDialog from "js/react/views/PresentationSettings/dialogs/EditLinkDialog";
import EditSocialShareDialog from "js/react/views/PresentationSettings/dialogs/EditSocialShareDialog";
import { Trans } from "react-i18next";
import { t } from "js/i18n";
const logger = getLogger(LogGroup.LINKS);

function trackLinkStatusChange(props) {
    trackActivity("Sharing", "ChangeLinkStatus", null, null, props, { audit: true });
}

function getPropsForDeleteLink(pendingLinkToDelete, type, presentation) {
    return {
        presentation_id: presentation.id,
        link_ids: [pendingLinkToDelete.get("id")],
        old_value: pendingLinkToDelete.get("enabled") ? "enabled" : "disabled",
        new_value: "deleted",
        workspace_id: presentation.getWorkspaceId(),
        type
    };
}

class LinkItem extends Component {
    componentDidMount() {
        this.props.link?.on("change", this.handleLinkChange);
    }

    componentWillUnmount() {
        this.props.link?.off("change", this.handleLinkChange);
    }

    handleLinkChange = () => this.forceUpdate();

    handleEditLink = () => {
        this.setState({ menuAnchorEl: null });
        this.props.onEditLink(this.props.link);
    };

    handleDisableLink = type => {
        const { presentation, link } = this.props;

        if (!WorkspaceController.actionPermissions[ActionPermissionsObject.PRESENTATION_LINKS_DISABLING].use) {
            this.setState({ menuAnchorEl: null });

            if (WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.PRESENTATION_LINKS_DISABLING, "use")) {
                ShowUpgradeDialog({
                    type: UpgradePlanDialogType.UPGRADE_PLAN,
                    analytics: { cta: "DisableLink", ...presentation.getAnalytics() },
                    workspaceId: WorkspaceController.workspaceId
                });
                return;
            }

            ShowWarningDialog({
                title: t("Oops!"),
                message: t("You don't have permission to disable links.")
            });
            return;
        }

        const analyticsProps = {
            presentation_id: presentation.id,
            link_ids: [link.get("id")],
            old_value: link.get("enabled") ? "enabled" : "disabled",
            new_value: !link.get("enabled") ? "enabled" : "disabled",
            workspace_id: presentation.getWorkspaceId(),
            type
        };
        link.update({ enabled: !link.get("enabled") });
        this.setState({ menuAnchorEl: null });
        trackLinkStatusChange(analyticsProps);
    };

    handleDeleteLink = () => {
        this.setState({ menuAnchorEl: null });
        this.props.onDeleteLink(this.props.link);
    };

    renderOptionsMenu(menuItems) {
        const { menuAnchorEl } = this.state;

        return (
            <Fragment>
                <Gap10 />
                <IconButton
                    onClick={event =>
                        this.setState({ menuAnchorEl: event.currentTarget })
                    }
                >
                    <Icon iconName="more_vert" style={{ color: "#333" }} />
                </IconButton>
                <Menu
                    id="menu"
                    anchorEl={menuAnchorEl}
                    open={Boolean(menuAnchorEl)}
                    onClose={() => this.setState({ menuAnchorEl: null })}
                >
                    {menuItems}
                </Menu>
            </Fragment>
        );
    }
}

class LinksList extends Component {
    state = {
        sortConfig: {
            key: "createdAt",
            direction: "desc"
        },
        sortedLinks: []
    };

    componentDidMount() {
        this.sortLinks();
    }

    componentDidUpdate(prevProps) {
        if (this.props.linksUpdated && this.props.linksUpdated !== prevProps.linksUpdated) {
            this.sortLinks();
            this.props.onLinksUpdated();
        }
    }

    sortLinks = () => {
        const { presentation } = this.props;
        const { sortConfig } = this.state;
        const allLinks = presentation.links.models;

        const sortedLinks = [...allLinks].sort((a, b) => {
            let aValue, bValue;

            if (sortConfig.key === "name") {
                aValue = a.get("name");
                bValue = b.get("name");
                return sortConfig.direction === "asc"
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            } else if (sortConfig.key === "createdAt") {
                aValue = new Date(a.get("createdAt"));
                bValue = new Date(b.get("createdAt"));
            } else if (sortConfig.key === "views") {
                aValue = a.get("views") || 0;
                bValue = b.get("views") || 0;
            }

            if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
            return 0;
        });

        this.setState({ sortedLinks });
    };

    handleSort = key => {
        this.setState(prevState => ({
            sortConfig: {
                key,
                direction: prevState.sortConfig.key === key && prevState.sortConfig.direction === "asc" ? "desc" : "asc"
            }
        }), this.sortLinks);
    };

    handleEditSharableLink = link => {
        const { presentation } = this.props;
        ShowDialog(EditLinkDialog, {
            link: link,
            presentation,
            callback: () => this.forceUpdate(),
            onClose: () => this.forceUpdate()
        });
    };

    handleEditInviteLink = link => {
        const { presentation } = this.props;
        ShowDialog(EditInviteDialog, {
            link: link,
            presentation,
            onClose: () => this.forceUpdate(),
            callback: expiresAt => {
                link.update({ expiresAt });
                this.forceUpdate();
            }
        });
    };

    handleEditEmbedLink = link => {
        const { presentation } = this.props;
        ShowDialog(EditEmbedCodeDialog, {
            link: link,
            presentation,
            callback: () => this.forceUpdate()
        });
    };

    handleEditSocialLink = link => {
        ShowDialog(EditSocialShareDialog, {
            link: link,
            presentation: this.props.presentation,
            onClose: () => this.forceUpdate(),
        });
    };

    handleDeleteLink = link => {
        const { presentation } = this.props;

        let message = (
            <p>
                <Trans
                    i18nKey={"Deleting the <strong>{{linkName}}</strong> link will immediately prevent access to this presentation using that URL."}
                    values={{ linkName: link.get("name") }}
                />
            </p>
        );
        switch (link.get("type")) {
            case LinkType.PUBLIC:
            case LinkType.PROTECTED:
                message = (
                    <p>
                        <Trans
                            i18nKey={"Deleting the <strong>{{linkName}}</strong> link will immediately prevent access to this presentation using that URL."}
                            values={{ linkName: link.get("name") }}
                        />
                    </p>
                );
                break;
            case LinkType.INVITE:
                message = (<>
                    <p>
                        <Trans
                            i18nKey={"Deleting the invitation to <strong>{{email}}</strong> will immediately revoke their permission to view this presentation."}
                            values={{ email: link.get("email") }}
                        />
                    </p>
                    <p>
                        <strong>
                            {t("This action cannot be undone and you will need to resend an invite if you want to restore permission.")}
                        </strong>
                    </p>
                </>);
                break;
            case LinkType.EMBED:
                message = (<>
                    <p>
                        {t("Deleting this embed will immediately disable playback on any websites, blogs, or other places the embed code has been added.")}
                    </p>
                    <p>
                        <strong>{t("This action cannot be undone.")}</strong>
                    </p>
                </>);
                break;
            case LinkType.SOCIAL:
                message = (<>
                    <p>
                        {t("Deleting the {{network}} social sharing link will immediately disable playback where that link was shared or reshared and cannot be undone.", { network: link.get("social") })}
                    </p>
                    <p>
                        {t("Please note that deleting this link will NOT delete the actual post or tweet containing the link from your social network. If you wish to delete the actual post or tweet, you must use the tools provided by the social network service.")}
                    </p>
                </>);
                break;
        }

        ShowConfirmationDialog({
            title: t("Delete Link?"),
            message,
            acceptCallback: () => {
                let linkType, loggerContext;
                switch (link.get("type")) {
                    case LinkType.PUBLIC:
                        linkType = "public_link";
                        loggerContext = "SharableLinksList";
                        break;
                    case LinkType.PROTECTED:
                        linkType = "protected_link";
                        loggerContext = "SharableLinksList";
                        break;
                    case LinkType.INVITE:
                        linkType = "invite_link";
                        loggerContext = "InvitesList";
                        break;
                    case LinkType.EMBED:
                        linkType = "embed_link";
                        loggerContext = "EmbedsList";
                        break;
                    case LinkType.SOCIAL:
                        linkType = "social_link";
                        loggerContext = "SocialLinksList";
                        break;
                    default:
                        logger.error("Unknown link type", { linkType: link.get("type") });
                        return;
                }

                linkDS
                    .deleteLink(presentation, link.id)
                    .then(() => {
                        trackLinkStatusChange(getPropsForDeleteLink(link, linkType, presentation));
                        this.sortLinks();
                    })
                    .catch(err => {
                        logger.error(err, `[${loggerContext}] handleDeleteLink() failed`, { presentationId: presentation.id, linkId: link.id });
                    });
            }
        });
    };

    renderLinkItem = link => {
        const { presentation } = this.props;

        const linkType = link.get("type");

        switch (linkType) {
            case LinkType.PUBLIC:
            case LinkType.PROTECTED:
                return (
                    <SharableLinkItem
                        key={link.id}
                        presentation={presentation}
                        link={link}
                        onEditLink={this.handleEditSharableLink}
                        onDeleteLink={this.handleDeleteLink}
                    />
                );
            case LinkType.INVITE:
                return (
                    <InviteLinkItem
                        key={link.id}
                        presentation={presentation}
                        link={link}
                        onEditLink={this.handleEditInviteLink}
                        onDeleteLink={this.handleDeleteLink}
                    />
                );
            case LinkType.EMBED:
                return (
                    <EmbedLinkItem
                        key={link.id}
                        presentation={presentation}
                        link={link}
                        onEditLink={this.handleEditEmbedLink}
                        onDeleteLink={this.handleDeleteLink}
                    />
                );
            case LinkType.SOCIAL:
                return (
                    <SocialLinkItem
                        key={link.id}
                        presentation={presentation}
                        link={link}
                        onEditLink={this.handleEditSocialLink}
                        onDeleteLink={this.handleDeleteLink}
                    />
                );
            default:
                return null;
        }
    };

    render() {
        const { sortConfig, sortedLinks } = this.state;
        if (sortedLinks.length) {
            return (
                <Fragment>
                    <div className="link-header">
                        <div className="name" onClick={() => this.handleSort("name")}>
                            {t("Name")} {sortConfig.key === "name" && (sortConfig.direction === "asc" ? "▲" : "▼")}
                        </div>
                        <div className="created" onClick={() => this.handleSort("createdAt")}>
                            {t("Created")} {sortConfig.key === "createdAt" && (sortConfig.direction === "asc" ? "▲" : "▼")}
                        </div>
                        <div className="view-state" onClick={() => this.handleSort("views")}>
                            {t("Views")} {sortConfig.key === "views" && (sortConfig.direction === "asc" ? "▲" : "▼")}
                        </div>
                    </div>
                    <div className="links">
                        {sortedLinks.map(link => this.renderLinkItem(link))}
                    </div>
                </Fragment>
            );
        } else {
            return (
                <div className="empty-list-warning">
                    {t("There are no links for this presentation")}
                </div>
            );
        }
    }
}

class SharableLinkItem extends LinkItem {
    state = {};
    embedRef = React.createRef();

    analyticsProps(link) {
        return {
            link_ids: [link.get("id")],
            is_new: false,
            type: "public_link",
            ...this.props.presentation.getAnalytics()
        };
    }

    copyToClipboard = () => {
        const { link } = this.props;

        clipboardWrite({
            [ClipboardType.TEXT]: this.embedRef.current.value,
        });
        ShowSnackBar({
            message: t("Shareable URL was copied to the clipboard")
        });
        const props = this.analyticsProps(link);
        trackActivity("Sharing", "CopyLink", null, null, props, { audit: true });
    };

    render() {
        const { presentation, link } = this.props;

        const viewCount = link.get("views") || 0;
        const privacySetting = presentation.getPrivacySetting();

        const enabled =
            link.get("enabled") && (
                (privacySetting === PresentationPrivacyType.PUBLIC && (link.get("type") === LinkType.PUBLIC || link.get("type") === LinkType.PROTECTED)) ||
                (privacySetting === PresentationPrivacyType.SECURED && link.get("type") === LinkType.PROTECTED)
            );

        return (
            <div className={`link ${enabled ? "" : "disabled-link"}`}>
                {link.get("type") === LinkType.PUBLIC && <Icon iconName="link" />}
                {link.get("type") === LinkType.PROTECTED && <Icon iconName="lock" />}
                <div className="name">{link.get("name")}</div>
                <div className="created">
                    {moment(link.get("createdAt")).format("MMM DD YYYY")}
                </div>

                <div className={`view-state ${viewCount > 0 ? "viewed" : ""}`}>
                    {viewCount} {viewCount === 1 ? t("view") : t("views")}
                </div>

                <div className="expires">
                    {(link.has("expiresAt") && Date.now() < new Date(link.get("expiresAt")).getTime()) &&
                        <span>
                            {t("Expires {{date}}", { date: moment(link.get("expiresAt")).format("MMM Do YYYY [at] h:mm a") })}
                        </span>
                    }
                    {(link.has("expiresAt") && Date.now() >= new Date(link.get("expiresAt")).getTime()) &&
                        <span style={{ color: "orangered" }}>
                            {t("Expired {{date}}", { date: moment(link.get("expiresAt")).format("MMM Do YYYY [at] h:mm a") })}
                        </span>
                    }
                </div>

                <FlexSpacer />
                <input
                    ref={this.embedRef}
                    className="link-url"
                    type="text"
                    value={link.getLinkURL()}
                    readOnly
                />
                {!enabled ? (
                    <div className="disabled-label">{t("Disabled")}</div>
                ) : (
                    <Button
                        size="small"
                        color="primary"
                        variant="contained"
                        disabled={link.isLinkExpired()}
                        onClick={this.copyToClipboard}
                    >
                        {t("Copy Link")}
                    </Button>
                )}

                {this.renderOptionsMenu([
                    <MenuItem key="edit" onClick={this.handleEditLink}>
                        {t("Edit Link...")}
                    </MenuItem>,
                    <MenuItem
                        key="disable"
                        onClick={() => this.handleDisableLink("public_link")}
                        divider
                    >
                        {enabled ? t("Disable Link") : t("Enable Link")}
                        <ProBadge
                            show={WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.PRESENTATION_LINKS_DISABLING, "use")}
                            analytics={{ cta: "DisableLink", ...this.analyticsProps(link) }}
                            workspaceId={presentation.getWorkspaceId()}
                        />
                    </MenuItem>,
                    <MenuItem key="delete" onClick={this.handleDeleteLink}>
                        {t("Delete Link")}
                    </MenuItem>
                ])}
            </div>
        );
    }
}

class InviteLinkItem extends LinkItem {
    state = {};

    analyticsProps(linkIds, recipientEmail) {
        const { presentation } = this.props;

        return {
            link_ids: linkIds,
            is_new: false,
            type: "email",
            sender_email: app.user.getEmail(),
            recipient_emails: [recipientEmail],
            ...presentation.getAnalytics()
        };
    }

    handleResendEmail = (linkIds, recipientEmail) => {
        const props = this.analyticsProps(linkIds, recipientEmail);
        trackActivity("Sharing", "EmailLink", null, null, props, { audit: true });

        Promise.all(linkIds.map(linkId => NotificationsService.notifyOnPresentationLinkCreated(linkId)))
            .then(() => {
                ShowSnackBar({
                    message: t("Invite was resent")
                });
            })
            .catch(err => {
                logger.error(err, "[InviteLinkItem] handleResendEmail() failed", { presentationId: this.global.presentation?.id });
            });
    };

    render() {
        let { presentation, link } = this.props;
        let viewCount = link.get("views") || 0;
        let enabled =
            link.get("enabled") &&
            presentation.getPrivacySetting() != PresentationPrivacyType.PRIVATE;

        return (
            <div className={`link ${enabled ? "" : "disabled-link"}`}>
                {this.renderInviteName(link)}
                <div className="created">
                    {moment(link.get("createdAt")).format("MMM DD YYYY")}
                </div>
                <div className={`view-state ${viewCount > 0 ? "viewed" : ""}`}>
                    {viewCount} {viewCount === 1 ? t("view") : t("views")}
                </div>

                <div className="expires">
                    {(link.has("expiresAt") && Date.now() < new Date(link.get("expiresAt")).getTime()) &&
                        <span>
                            {t("Expires {{date}}", { date: moment(link.get("expiresAt")).format("MMM Do YYYY [at] h:mm a") })}
                        </span>
                    }
                    {(link.has("expiresAt") && Date.now() >= new Date(link.get("expiresAt")).getTime()) &&
                        <span style={{ color: "orangered" }}>
                            {t("Expired {{date}}", { date: moment(link.get("expiresAt")).format("MMM Do YYYY [at] h:mm a") })}
                        </span>
                    }
                </div>

                <FlexSpacer />

                {!enabled ? (
                    <div className="disabled-label">{t("Disabled")}</div>
                ) : (
                    <Button
                        size="small"
                        color="primary"
                        variant="contained"
                        onClick={() => {
                            this.handleResendEmail(
                                [link.id],
                                link.get("email")
                            );
                        }}
                    >
                        {t("Resend Email")}
                    </Button>
                )}

                {this.renderOptionsMenu([
                    <MenuItem key="edit" onClick={this.handleEditLink}>
                        {t("Edit Link...")}
                    </MenuItem>,
                    <MenuItem
                        key="disable"
                        onClick={() => this.handleDisableLink("invite")}
                        divider
                    >
                        {link.get("enabled") ? t("Disable Link") : t("Enable Link")}
                        {
                            <ProBadge
                                show={WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.PRESENTATION_LINKS_DISABLING, "use")}
                                analytics={{
                                    cta: "DisableLink", ...this.analyticsProps(
                                        [link.id],
                                        link.get("email")
                                    )
                                }}
                                workspaceId={presentation.getWorkspaceId()}
                                style={{ right: -10, position: "relative" }}
                            />
                        }
                    </MenuItem>,
                    <MenuItem key="delete" onClick={this.handleDeleteLink}>
                        {t("Delete Link")}
                    </MenuItem>
                ])}
            </div>
        );
    }

    renderInviteName(link) {
        let contact = link.get("email");
        if (typeof contact == "object") {
            return (
                <Fragment>
                    <Avatar url={contact.photoUrl} />
                    <div className="name">{contact.email}</div>
                </Fragment>
            );
        } else {
            return (
                <Fragment>
                    <Avatar url="" />
                    <div className="name">{contact}</div>
                </Fragment>
            );
        }
    }
}

class EmbedLinkItem extends LinkItem {
    state = {};

    embedRef = React.createRef();

    copyToClipboard = () => {
        const { link } = this.props;

        clipboardWrite(
            {
                [ClipboardType.HTML]: this.embedRef.current.value,
                [ClipboardType.TEXT]: this.embedRef.current.value,
            },
            true,
        );
        ShowSnackBar({
            message: t("Embed code was copied to the clipboard")
        });
        const props = this.analyticsProps(link);
        trackActivity("Sharing", "CopyLink", null, null, props, { audit: true });
    };

    analyticsProps(link) {
        return {
            link_ids: [link.get("id")],
            is_new: false,
            type: "embed_code",
            ...this.props.presentation.getAnalytics()
        };
    }

    render() {
        let { presentation, link } = this.props;

        let viewCount = link.get("views") || 0;
        let enabled =
            link.get("enabled") && (
                presentation.getPrivacySetting() == PresentationPrivacyType.PUBLIC ||
                (link.get("domainWhitelist") && presentation.getPrivacySetting() === PresentationPrivacyType.SECURED)
            );

        return (
            <div className={`link ${enabled ? "" : "disabled-link"}`}>
                <Icon iconName="add_to_queue" />
                <div className="name">{link.get("name")}</div>
                <div className="created">
                    {moment(link.get("createdAt")).format("MMM DD YYYY")}
                </div>
                <div className={`view-state ${viewCount > 0 ? "viewed" : ""}`}>
                    {viewCount} {viewCount === 1 ? t("view") : t("views")}
                </div>

                <FlexSpacer />

                <input
                    ref={this.embedRef}
                    className="link-url"
                    type="text"
                    value={presentation.getEmbedCode(link)}
                    readOnly
                />
                {!enabled ? (
                    <div className="disabled-label">{t("Disabled")}</div>
                ) : (
                    <Button
                        size="small"
                        color="primary"
                        variant="contained"
                        onClick={this.copyToClipboard}
                    >
                        {t("Copy Embed Code")}
                    </Button>
                )}
                {this.renderOptionsMenu([
                    <MenuItem key="edit" onClick={this.handleEditLink} divider>
                        {t("Edit Embed...")}
                    </MenuItem>,
                    <MenuItem
                        key="disable"
                        onClick={() => this.handleDisableLink("embed_code")}
                        divider
                    >
                        {link.get("enabled") ? t("Disable Embed") : t("Enable Embed")}
                        {
                            <ProBadge
                                show={WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.PRESENTATION_LINKS_DISABLING, "use")}
                                analytics={{ cta: "DisableLink", ...this.analyticsProps(link) }}
                                workspaceId={presentation.getWorkspaceId()}
                                style={{ right: -10, position: "relative" }}
                            />
                        }
                    </MenuItem>,
                    <MenuItem key="delete" onClick={this.handleDeleteLink}>
                        {t("Delete Embed")}
                    </MenuItem>
                ])}
            </div>
        );
    }
}

class SocialLinkItem extends LinkItem {
    state = {};

    embedRef = React.createRef();

    analyticsProps() {
        return {
            type: "social",
            ...this.props.presentation.getAnalytics()
        };
    }

    copyToClipboard = () => {
        clipboardWrite({
            [ClipboardType.TEXT]: this.embedRef.current.value,
        });
        ShowSnackBar({
            message: t("Embed code was copied to the clipboard")
        });
    };

    render() {
        let { presentation, link } = this.props;
        let viewCount = link.get("views") || 0;
        let enabled =
            link.get("enabled") &&
            presentation.getPrivacySetting() == PresentationPrivacyType.PUBLIC;

        return (
            <div className={`link ${enabled ? "" : "disabled-link"}`}>
                <Icon iconName="share" />
                <div className="name">{link.getLinkName()}</div>
                <div className="created">
                    {moment(link.get("createdAt")).format("MMM DD YYYY")}
                </div>
                <div className={`view-state ${viewCount > 0 ? "viewed" : ""}`}>
                    {viewCount} {viewCount === 1 ? t("view") : t("views")}
                </div>

                <FlexSpacer />

                <input
                    ref={this.embedRef}
                    className="link-url"
                    type="text"
                    value={link.getLinkURL()}
                    readOnly
                />
                {!enabled && <div className="disabled-label">{t("Disabled")}</div>}
                {this.renderOptionsMenu([
                    <MenuItem key="edit" onClick={this.handleEditLink} divider>
                        {t("Edit Link...")}
                    </MenuItem>,
                    <MenuItem
                        key="disable"
                        onClick={() =>
                            this.handleDisableLink(
                                link.getLinkName().toLowerCase()
                            )
                        }
                        divider
                    >
                        {link.get("enabled") ? t("Disable Link") : t("Enable Link")}
                        {
                            <ProBadge
                                show={WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.PRESENTATION_LINKS_DISABLING, "use")}
                                analytics={{ cta: "DisableLink", ...this.analyticsProps() }}
                                workspaceId={presentation.getWorkspaceId()}
                                style={{ right: -10, position: "relative" }}
                            />
                        }
                    </MenuItem>,
                    <MenuItem key="delete" onClick={this.handleDeleteLink}>
                        {t("Delete Link")}
                    </MenuItem>
                ])}
            </div>
        );
    }
}

export { LinksList };
